/**
 * DRC-1 — single canonical `computeReportDocStats` helper.
 *
 * Six different doc counts were surfacing on the same Redhead site
 * (three of them on the same report), each derived from a different
 * formula. The "4 excluded" subhead specifically conflated TWO
 * categories: docs the user actively excluded vs docs deleted from
 * the site since the report was generated. This helper produces ONE
 * stat bundle the consuming surfaces (ReferencesSection subhead,
 * Reports tab row chip, future header chip, Documents tab, Site card)
 * all consume, so the math reconciles cleanly across surfaces.
 *
 * Math invariant the helper pins (and the test pack guards):
 *
 *   totalRefs === includedAndLive + excludedByUser + droppedAsMissing
 *
 *   visibleLatestCount + hiddenRevisionsCount === resolvedRefs
 *   (after revision-dedupe collapse of the live-resolved subset)
 *
 * The legacy "X included · Y excluded" formulation is a misread of
 * `ReportRef.userIncluded`: a ref where the user toggled the doc on
 * but the doc was later deleted from the site still carried
 * `userIncluded === true`. The headline counted it as "included" but
 * the dedup/render layer silently dropped it because
 * `docsById.get(ref.docId)` returned undefined. So the cited
 * appendix would list 73 dedup-collapsed leaders and the subhead
 * would shout "81 included" — a 12-row delta the UI never explained.
 *
 * `computeReportDocStats` splits that into three honest buckets:
 *   - `includedAndLive`     — user said yes AND doc still on site
 *   - `excludedByUser`      — user said no (carries `reasonIfExcluded`)
 *   - `droppedAsMissing`    — user said yes BUT doc deleted from site
 *
 * Pure projection — never mutates `report` / `site`, never throws.
 * Memo-friendly at the React caller (keyed on `report` / `site`
 * identity + the dedupe flag).
 *
 * Composability:
 *   - RCO (shipped, 0.99219) added `computeBodyCitedDocIds` for the
 *     "which docs did the narrative actually cite?" question. This
 *     helper answers the orthogonal "which refs survived as live
 *     site.documents?" question. The ReferencesSection consumer
 *     uses BOTH: RCO partitions `includedAndLive` into cited vs
 *     provided-uncited; DRC reveals the dropped-as-missing slice
 *     that previously hid inside `excludedCount`.
 *   - EDX-4 (shipped, 0.99192) added revision-dedupe. The helper
 *     calls into `dedupeDocsByCanonicalName` so the
 *     `visibleLatestCount` / `hiddenRevisionsCount` numbers stay
 *     consistent with the appendix's dedupe banner.
 *   - DDL (queued, 0.99204) fixes the union mass-stamp at upload/
 *     delete time; DRC fixes the display-side divergence. Both
 *     improve count consistency. Long-term DDL-FOLLOWUP-IDREF
 *     replaces both.
 */
import type { SiteRagReport, SiteDocument } from '../types/siteRag';
import type { SiteEntity } from '../types/site';
import type { SiteEvidenceDocument } from '../types/siteEvidence';
import { dedupeDocsByCanonicalName, countHiddenRevisions } from './documentDedupe';
import { isLibraryIndexDoc } from './knowledge/libraryIndexDocument';
import {
  collectNarrativeBlob,
  computeBodyCitedDocIds,
  matchDocByFilename,
  PER_FILENAME_RE,
} from './reportCitedDocIds';
import { resolveReferencedDocumentId } from './resolveDocumentLineage';
import { extractNumberedRefIds } from './citationTokens';
// DDL-FOLLOWUP-IDREF — reads through resolveSiteDocuments for fresh state
import { resolveSiteDocuments, withResolvedSiteDocumentFileName } from './site/resolveSiteDocuments';

export interface ReportDocStats {
  /** All refs stamped at gen time. `report.documentRefs?.length ?? 0`. */
  totalRefs: number;
  /**
   * Refs that still resolve in live `site.documents` — regardless of
   * `userIncluded`. Useful for sanity checks; rarely surfaced
   * directly. `includedAndLive + (excludedByUser ∩ live)` ≤ this.
   */
  resolvedRefs: number;
  /**
   * Refs where `userIncluded === true` AND the doc still resolves in
   * live `site.documents`. This is the canonical "documents informing
   * this report" headline number — every consumer surface
   * (Reports row chip, Documents-informing subhead, future header
   * chip) renders this so the three numbers align.
   */
  includedAndLive: number;
  /**
   * Refs where `userIncluded === false`. Carries `reasonIfExcluded`.
   * Same regardless of whether the underlying doc still resolves —
   * the user's explicit "no" stands even if the doc was later
   * deleted from the site.
   */
  excludedByUser: number;
  /**
   * Refs where `userIncluded === true` BUT the doc no longer
   * resolves in live `site.documents` — i.e. silently deleted from
   * the site after the report was generated. Previously folded
   * into the misleading "excluded" tally; now broken out so the
   * subhead can render an honest "N deleted from site since report
   * ran" affordance.
   */
  droppedAsMissing: number;
  /**
   * Latest-revision count after `dedupeDocsByCanonicalName`
   * collapses earlier revisions of the same canonical document.
   * Drives the "Showing N latest documents" chip in the appendix.
   * Always ≤ `includedAndLive`.
   */
  visibleLatestCount: number;
  /**
   * Earlier revisions hidden under the dedupe collapse — sum across
   * all groups. Drives the "M earlier revisions hidden" chip.
   * `visibleLatestCount + hiddenRevisionsCount === includedAndLive`
   * (when `dedupeRevisions === true`).
   */
  hiddenRevisionsCount: number;
  /**
   * Live `site.documents.length` filtered to non-library-index
   * docs — the union-surface total that the Site card chip and the
   * Documents tab badge render. Provided here so the
   * "of N site total today" tail of the Documents-informing subhead
   * pulls from the same helper as everything else.
   */
  siteTotalLive: number;
  /**
   * RCO-3 — docIds that ACTUALLY produced a body citation in the
   * narrative (either `[Ref N]` resolved through SED lineage OR
   * `ReportClaim.sources[]` carrying `doc:<id>`). Populated only
   * when the caller supplies `opts.sed`; otherwise `undefined`
   * (callers gate on presence to decide whether the "cited" split
   * is available for that surface).
   *
   * The Documents-informing panel partitions `includedAndLive` into
   *   cited       = includedAndLive ∩ citedDocIds
   *   providedButUncited = includedAndLive \ citedDocIds
   *
   * Header switches from "81 included" to "28 cited · 53 provided
   * but not cited · 4 excluded by user" — the honest math.
   */
  citedDocIds?: ReadonlyArray<string>;
  /**
   * RCO-3 — `includedAndLive` partition broken out by citation
   * fact. `providedButUncited` is the diff that surfaces in the
   * collapsed disclosure ("These documents were included in the
   * prompt context but the narrative didn't cite them").
   *
   * Both arrays preserve `documentRefs` ordering so the panel's
   * existing sort comparator continues to work. Empty arrays when
   * `opts.sed` is undefined.
   */
  citedRefDocIds: ReadonlyArray<string>;
  providedButUncitedDocIds: ReadonlyArray<string>;
  /**
   * RCO-3 — `excludedByUser` partition's actual docId list. Pack
   * RCO-3 calls this out explicitly so consumers (admin diagnostic
   * panel, audit CSV export) can lift the docIds out of the stats
   * bundle without re-walking `documentRefs`. Order follows
   * `documentRefs` (= insertion order).
   */
  excludedByUserDocIds: ReadonlyArray<string>;
  /**
   * RCO-3 — count of docs whose `citedRefDocIds` membership came
   * from the narrative citation pipeline (Path A + Path B union).
   * Equal to `citedRefDocIds.length` when `opts.sed` is provided,
   * 0 otherwise. Headline number for the "X cited" subhead.
   */
  citedRefs: number;
  /**
   * RCO-3 — count of docs in `providedButUncitedDocIds`. Equal to
   * `includedAndLive - citedRefs` when `opts.sed` is provided,
   * 0 otherwise (legacy callers see all `includedAndLive` rows).
   */
  providedUncitedRefs: number;
  /**
   * R-74 — distinct cited knowledge-base / external-literature references.
   * Provider, intent and unresolved document references are not KB entries.
   */
  knowledgeBaseCitedCount: number;
  /**
   * R-74 — count of `(per "filename")` inline citations resolved to
   * live uploaded docs.
   */
  inlineFilenameCitedCount: number;
}

/**
 * Options for `computeReportDocStats`. Defaults match the
 * ReferencesSection appendix's current behaviour: revision-dedupe ON.
 */
export interface ComputeReportDocStatsOpts {
  /**
   * When false, `visibleLatestCount === includedAndLive` and
   * `hiddenRevisionsCount === 0` — the helper still runs the dedupe
   * walk so the math is deterministic, but reports the un-collapsed
   * totals. Mirrors the "Show all" admin chip in the appendix.
   */
  dedupeRevisions?: boolean;
  /**
   * RCO-3 — optional SED used to compute the citation-fact split.
   *   - Provided ⇒ `citedDocIds` / `citedRefDocIds` /
   *     `providedButUncitedDocIds` / `citedRefs` /
   *     `providedUncitedRefs` are populated via
   *     `computeBodyCitedDocIds(report, sed, { liveDocs })`.
   *   - Omitted ⇒ `citedDocIds === undefined` and the split fields
   *     fall back to empty / zero so legacy callers (DRC-1's
   *     existing surfaces) keep their math unchanged.
   *
   * Path C in `computeBodyCitedDocIds` additionally requires live docs
   * on `site.documents` or `report.documents`.
   *
   * The SED carries the per-section `linkedDocumentRefs[*].docId`
   * lineage that `[Ref N]` walks resolve through, so without it
   * Path A in `computeBodyCitedDocIds` returns the empty set.
   */
  sed?: SiteEvidenceDocument | undefined;
}

function collectCitedRefIndices(report: SiteRagReport): Set<number> {
  return new Set(extractNumberedRefIds(collectNarrativeBlob(report)));
}

/**
 * Compute one canonical stats bundle for a report's document
 * footprint. Every doc-count surface across the app should consume
 * this helper rather than re-deriving from `documentRefs.filter(…)`.
 */
export function computeReportDocStats(
  report: SiteRagReport,
  site: SiteEntity | undefined,
  opts: ComputeReportDocStatsOpts = {},
): ReportDocStats {
  const { dedupeRevisions = true, sed } = opts;
  const refs = report.documentRefs ?? [];
  // Resolve against live `site.documents` first, with a legacy
  // fallback to the inline `report.documents` array for pre-Prompt-1
  // reports that haven't migrated. Both paths run through the same
  // by-id map so the "is this docId still live?" check is uniform.
  const liveDocsList = (site?.documents ?? report.documents ?? []).map((d, idx) =>
    withResolvedSiteDocumentFileName(d, `doc-${idx}`),
  );
  const liveDocsById = new Map<string, SiteDocument>(
    liveDocsList.map((d) => [d.id, d]),
  );
  const isLive = (docId: string): boolean => liveDocsById.has(docId);

  let totalRefs = 0;
  let resolvedRefs = 0;
  let includedAndLive = 0;
  let excludedByUser = 0;
  let droppedAsMissing = 0;
  const includedLiveDocs: SiteDocument[] = [];
  // RCO-3 — keep insertion-ordered docId arrays so the partition is
  // stable. `includedAndLiveDocIds` is the universe RCO splits into
  // cited vs provided-but-uncited.
  const includedAndLiveDocIds: string[] = [];
  const excludedByUserDocIds: string[] = [];

  // Legacy-fallback path — pre-Prompt-1 reports carried the docs
  // inline on `report.documents` and never stamped `documentRefs`.
  // For those, `getReportDocuments` returns `report.documents` and
  // every doc is implicitly "included and live". The helper mirrors
  // that contract here so the counts agree with the legacy chip math
  // (e.g. the Coverage breadcrumb's "N documents reviewed" string).
  //
  // DDL-FOLLOWUP-IDREF — reads through resolveSiteDocuments for fresh state.
  // When the legacy snapshot path triggers, route through the resolver
  // so that IDREF-era reports (which carry `documentIds` but no
  // `documentRefs` — none in prod today, but the field is now defined)
  // pick up live `site.documents` rows rather than the snapshot. The
  // resolver's contract guarantees ordering + filters missing ids, so
  // the count math stays consistent with what consumers see rendered.
  const legacyLiveDocs = resolveSiteDocuments(site, report);
  if (refs.length === 0 && legacyLiveDocs.length > 0) {
    for (const d of legacyLiveDocs) {
      totalRefs += 1;
      resolvedRefs += 1;
      includedAndLive += 1;
      includedLiveDocs.push(d);
      includedAndLiveDocIds.push(d.id);
    }
  } else {
    for (const ref of refs) {
      totalRefs += 1;
      const live = isLive(ref.docId);
      if (live) resolvedRefs += 1;
      if (ref.userIncluded) {
        if (live) {
          includedAndLive += 1;
          const doc = liveDocsById.get(ref.docId);
          if (doc) includedLiveDocs.push(doc);
          includedAndLiveDocIds.push(ref.docId);
        } else {
          droppedAsMissing += 1;
        }
      } else {
        excludedByUser += 1;
        excludedByUserDocIds.push(ref.docId);
      }
    }
  }

  const groups = dedupeDocsByCanonicalName(includedLiveDocs);
  const hiddenRevisionsCount = dedupeRevisions
    ? countHiddenRevisions(groups)
    : 0;
  const visibleLatestCount = dedupeRevisions
    ? groups.length
    : includedAndLive;

  // Site card / Documents-tab style total: filter out the auto-
  // generated library-index doc so the count matches what the user
  // actually sees in the library UI.
  const siteTotalLive = (site?.documents ?? []).filter(
    (d) => !isLibraryIndexDoc(d),
  ).length;

  const referencesCount = Array.isArray(report.references) ? report.references.length : 0;
  const citedIdxs = collectCitedRefIndices(report);
  let citedRefs = 0;
  for (const idx of citedIdxs) {
    if (idx >= 1 && idx <= referencesCount) citedRefs += 1;
  }
  let providedUncitedRefs = Math.max(0, referencesCount - citedRefs);

  let knowledgeBaseCitedCount = 0;
  if (sed && Array.isArray(report.references) && report.references.length > 0) {
    // Knowledge entries use their catalog id (or the DAM's kb-<id> form);
    // external literature has its own explicit field. Failed document lineage
    // or a provider/intent field does not establish a knowledge-base source.
    const knowledgeIds = new Set((sed.knowledgeBase ?? []).map(entry => entry.id));
    for (const idx of citedIdxs) {
      const ref = report.references[idx - 1];
      if (!ref) continue;
      const docId = resolveReferencedDocumentId(ref, sed);
      if (!docId && typeof ref.field === 'string' && (knowledgeIds.has(ref.field) || ref.field.startsWith('kb-')
        || ref.field === 'external-literature' || ref.field === 'externalLiterature')) {
        knowledgeBaseCitedCount += 1;
      }
    }
  }

  let inlineFilenameCitedCount = 0;
  if (liveDocsList.length > 0) {
    const blob = collectNarrativeBlob(report);
    if (blob.length > 0) {
      PER_FILENAME_RE.lastIndex = 0;
      let fm: RegExpExecArray | null;
      while ((fm = PER_FILENAME_RE.exec(blob))) {
        const fn = fm[1].trim();
        if (matchDocByFilename(fn, liveDocsList)) inlineFilenameCitedCount += 1;
      }
    }
  }

  // RCO-3 — doc-id partition only resolves when a SED is provided.
  let citedDocIds: ReadonlyArray<string> | undefined;
  let citedRefDocIds: string[] = [];
  let providedButUncitedDocIds: string[] = [];
  if (sed) {
    const citedSet = computeBodyCitedDocIds(report, sed, { liveDocs: liveDocsList });
    citedDocIds = Array.from(citedSet);
    for (const id of includedAndLiveDocIds) {
      if (citedSet.has(id)) citedRefDocIds.push(id);
      else providedButUncitedDocIds.push(id);
    }
    citedRefs = citedRefDocIds.length;
    providedUncitedRefs = providedButUncitedDocIds.length;
  }

  return {
    totalRefs,
    resolvedRefs,
    includedAndLive,
    excludedByUser,
    droppedAsMissing,
    visibleLatestCount,
    hiddenRevisionsCount,
    siteTotalLive,
    citedDocIds,
    citedRefDocIds,
    providedButUncitedDocIds,
    excludedByUserDocIds,
    citedRefs,
    providedUncitedRefs,
    knowledgeBaseCitedCount,
    inlineFilenameCitedCount,
  };
}
